import { makeUploader, UploadState } from 'client/lib/rpx-client';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import type { FilePickerResult } from './index';

export type BatchItemStatus =
  | 'pending'
  | 'uploading'
  | 'success'
  | 'failed'
  | 'skipped'
  | 'canceled';

export type BatchUploadItem = {
  id: string;
  file: File;
  status: BatchItemStatus;
  progress: number;
  error?: string;
  result?: FilePickerResult;
};

export type BatchPhase = 'ready' | 'uploading' | 'paused' | 'complete' | 'canceled';

export type BatchSnapshot = {
  items: BatchUploadItem[];
  phase: BatchPhase;
};

type UploadHandle = {
  upload(): Promise<FilePickerResult | undefined>;
  abort(): Promise<void>;
};

type UploadFactory = (opts: {
  file: File;
  folderId?: string;
  isPublic?: boolean;
  onProgress(state: UploadState): void;
}) => UploadHandle;

type BatchOptions = {
  folderId?: string;
  isPublic?: boolean;
  uploadFactory?: UploadFactory;
};

function uploadErrorMessage(err: unknown) {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err && 'message' in err && typeof err.message === 'string') {
    return err.message;
  }
  return 'Upload failed.';
}

export class SequentialUploadController {
  private snapshot: BatchSnapshot;
  private listeners = new Set<(snapshot: BatchSnapshot) => void>();
  private activeUploader?: UploadHandle;
  private uploaders = new Map<string, UploadHandle>();
  private running?: Promise<void>;
  private cancelRequested = false;
  private disposed = false;

  constructor(
    files: File[],
    private opts: BatchOptions,
  ) {
    this.snapshot = {
      phase: 'ready',
      items: files.map((file, index) => ({
        id: `upload-${index}`,
        file,
        status: 'pending',
        progress: 0,
      })),
    };
  }

  getSnapshot() {
    return this.snapshot;
  }

  getSuccessfulResults() {
    return this.snapshot.items.flatMap((item) => (item.result ? [item.result] : []));
  }

  subscribe(listener: (snapshot: BatchSnapshot) => void) {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  start() {
    if (this.running || this.disposed) {
      return this.running ?? Promise.resolve();
    }
    this.cancelRequested = false;
    this.setPhase('uploading');
    this.running = this.runPending().finally(() => {
      this.running = undefined;
    });
    return this.running;
  }

  retry(itemId: string) {
    if (this.running || this.disposed) {
      return this.running ?? Promise.resolve();
    }
    this.updateItem(itemId, (item) => ({
      ...item,
      status: 'pending',
      progress: 0,
      error: undefined,
    }));
    return this.start();
  }

  skip(itemId: string) {
    if (this.running || this.disposed) {
      return this.running ?? Promise.resolve();
    }
    void this.uploaders.get(itemId)?.abort();
    this.uploaders.delete(itemId);
    this.updateItem(itemId, (item) => ({ ...item, status: 'skipped' }));
    return this.start();
  }

  replaceFile(itemId: string, file: File) {
    if (this.snapshot.phase !== 'ready' || this.disposed) {
      return;
    }
    this.updateItem(itemId, (item) => ({ ...item, file }));
  }

  async cancel() {
    if (this.disposed) {
      return this.getSuccessfulResults();
    }
    this.cancelRequested = true;
    await this.activeUploader?.abort();
    await this.running;
    this.replaceSnapshot({
      phase: 'canceled',
      items: this.snapshot.items.map((item) =>
        item.status === 'pending' || item.status === 'uploading'
          ? { ...item, status: 'canceled' }
          : item,
      ),
    });
    this.uploaders.clear();
    return this.getSuccessfulResults();
  }

  dispose() {
    this.disposed = true;
    this.cancelRequested = true;
    void this.activeUploader?.abort();
    this.uploaders.clear();
    this.listeners.clear();
  }

  private async runPending() {
    while (!this.cancelRequested && !this.disposed) {
      const item = this.snapshot.items.find((candidate) => candidate.status === 'pending');
      if (!item) {
        this.setPhase('complete');
        return;
      }

      this.updateItem(item.id, (current) => ({
        ...current,
        status: 'uploading',
        progress: 0,
        error: undefined,
      }));
      const uploadFactory = this.opts.uploadFactory ?? makeUploader;
      const uploader =
        this.uploaders.get(item.id) ??
        uploadFactory({
          file: item.file,
          folderId: this.opts.folderId,
          isPublic: this.opts.isPublic,
          onProgress: ({ progress }) => {
            this.updateItem(item.id, (current) => ({ ...current, progress }));
          },
        });
      this.uploaders.set(item.id, uploader);
      this.activeUploader = uploader;

      try {
        const result = await uploader.upload();
        if (result) {
          this.uploaders.delete(item.id);
          this.updateItem(item.id, (current) => ({
            ...current,
            status: 'success',
            progress: 100,
            result,
          }));
        } else if (!this.cancelRequested) {
          throw new Error('Upload stopped before the file was saved.');
        }
      } catch (err) {
        if (!this.cancelRequested) {
          this.updateItem(item.id, (current) => ({
            ...current,
            status: 'failed',
            error: uploadErrorMessage(err),
          }));
          this.setPhase('paused');
          return;
        }
      } finally {
        if (this.activeUploader === uploader) {
          this.activeUploader = undefined;
        }
      }
    }
  }

  private setPhase(phase: BatchPhase) {
    this.replaceSnapshot({ ...this.snapshot, phase });
  }

  private updateItem(id: string, update: (item: BatchUploadItem) => BatchUploadItem) {
    this.replaceSnapshot({
      ...this.snapshot,
      items: this.snapshot.items.map((item) => (item.id === id ? update(item) : item)),
    });
  }

  private replaceSnapshot(snapshot: BatchSnapshot) {
    this.snapshot = snapshot;
    if (!this.disposed) {
      this.listeners.forEach((listener) => listener(snapshot));
    }
  }
}

export function useSequentialUploads(files: File[], opts: BatchOptions) {
  const controller = useMemo(() => new SequentialUploadController(files, opts), []);
  const [snapshot, setSnapshot] = useState(controller.getSnapshot());

  useEffect(() => {
    const unsubscribe = controller.subscribe(setSnapshot);
    return () => {
      unsubscribe();
      controller.dispose();
    };
  }, [controller]);

  return {
    ...snapshot,
    start: useCallback(() => controller.start(), [controller]),
    retry: useCallback((id: string) => controller.retry(id), [controller]),
    skip: useCallback((id: string) => controller.skip(id), [controller]),
    replaceFile: useCallback(
      (id: string, file: File) => controller.replaceFile(id, file),
      [controller],
    ),
    cancel: useCallback(() => controller.cancel(), [controller]),
    successfulResults: controller.getSuccessfulResults(),
  };
}
